import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import authService from './services/authService';
import { AuthProvider } from './context/AuthContext';
import AppLayout from './components/layout/AppLayout';
import Login from './pages/Login';
import Logout from './pages/Logout';
import ModulePage from './pages/ModulePage';
import UsersAdmin from './pages/UsersAdmin';
import Schedule from './pages/Schedule/Schedule';
import Settings from './pages/Settings/Settings';
import AssistantPage from './pages/Assistant/AssistantPage';
import CodesTariffs from './pages/Codes/CodesTariffs';
import Patients from './pages/Patients/Patients';
import BillingClaims from './pages/Billing/BillingClaims';
import Surgeons from './pages/Surgeons/Surgeons';
import Quotes from './pages/Quotes/Quotes';
import PublicQuote from './pages/Quote/PublicQuote';
import PreopList from './pages/Preop/PreopList';
import PublicPreop from './pages/Preop/PublicPreop';
import PreopQuestions from './pages/Preop/PreopQuestions';
import Dashboard from './pages/Dashboard/Dashboard';
import Debtors from './pages/Debtors/Debtors';
import PublicOnboarding from './pages/Onboard/PublicOnboarding';
import { navForRole } from './config/navigation';

// Routes whose path renders a real page instead of the generic ModulePage.
const CUSTOM_PAGES = {
  '/dashboard': Dashboard,
  '/debtors': Debtors,
  '/users': UsersAdmin,
  '/schedule': Schedule,
  '/settings': Settings,
  '/assistant': AssistantPage,
  '/codes': CodesTariffs,
  '/patients': Patients,
  '/billing': BillingClaims,
  '/surgeons': Surgeons,
  '/quotes': Quotes,
  '/preop': PreopList,
  '/preop-questions': PreopQuestions,
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => authService.isAuthenticated());

  // Unauthenticated: only the login / logout screens exist.
  if (!isAuthenticated) {
    return (
      <Router>
        <Routes>
          {/* The patient onboarding link is public — it works whether or not anyone is logged in. */}
          <Route path="/onboard/:token" element={<PublicOnboarding />} />
          <Route path="/quote/:token" element={<PublicQuote />} />
          <Route path="/preop/:token" element={<PublicPreop />} />
          <Route path="/login" element={<Login onLoginSuccess={() => setIsAuthenticated(true)} />} />
          <Route path="/logout" element={<Logout onLogout={() => setIsAuthenticated(false)} />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    );
  }

  // Authenticated: build routes from the current user's role-based nav.
  const role = authService.getRole();
  const navItems = navForRole(role);

  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/onboard/:token" element={<PublicOnboarding />} />
          <Route path="/quote/:token" element={<PublicQuote />} />
          <Route path="/preop/:token" element={<PublicPreop />} />
          <Route path="/logout" element={<Logout onLogout={() => setIsAuthenticated(false)} />} />
          <Route
            path="*"
            element={
              <AppLayout>
                <Routes>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  {navItems.map((item) => {
                    const Custom = CUSTOM_PAGES[item.path];
                    return (
                      <Route
                        key={item.path}
                        path={item.path}
                        element={
                          Custom
                            ? <Custom />
                            : <ModulePage title={item.title} subtitle={item.subtitle} features={item.features} />
                        }
                      />
                    );
                  })}
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </AppLayout>
            }
          />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
